// Centralized SSRF guards for the app's media fetch/upload path.
//
// There are exactly two network sinks for media, and both live behind a guard here:
//   1. fetchMediaAsFile() below — the ONLY place a media URL is fetched. It runs the
//      same-origin guard immediately before the fetch.
//   2. axios.post(uploadUrl) inside API_IMAGE_UPLOAD (api/configurationScreenServices.js)
//      — the ONLY upload sink. It runs assertHttpsUploadUrl immediately before the post.
// Every widget/upload site reaches both through configurationScreenServices.
// uploadMediaToBucket(), so these guards cover all media traffic even though this module
// is imported directly only by configurationScreenServices. (The one other fetch in the
// tree, DynamicSVGComponent, loads a bundled same-origin icon, not config-driven media.)

// Only media whose origin is this application may be fetched. blob:/data: URLs are
// inherently same-origin/inline, so they are allowed directly; everything else must
// match the application's own origin, otherwise the request is blocked.
const allowedMediaOrigins = new Set([window.location.origin]);

export const assertSameOriginMedia = (url) => {
  const mediaUrl = String(url ?? '');
  if (mediaUrl.startsWith('blob:') || mediaUrl.startsWith('data:')) {
    return mediaUrl;
  }
  let mediaOrigin = '';
  try {
    mediaOrigin = new URL(mediaUrl, window.location.origin).origin;
  } catch (e) {
    mediaOrigin = '';
  }
  if (!allowedMediaOrigins.has(mediaOrigin)) {
    throw new Error('Blocked request to an untrusted URL');
  }
  return mediaUrl;
};

// Single choke point for turning a local media URL (the blob:/data: URL the app itself
// produced via the cropper / html2canvas) into a File for upload. The same-origin guard
// runs immediately before the fetch, so this is the one place a media URL is fetched.
export const fetchMediaAsFile = async (url, fileName) => {
  assertSameOriginMedia(url);
  const res = await fetch(url);
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type });
};

// Hostnames that must never be an upload target even over https: loopback, the cloud
// link-local metadata range (169.254.169.254), and RFC1918 private ranges. An
// https-scheme check alone would still allow these if the signed URL were ever tainted,
// so we reject them explicitly. Note: this blocks IP-literal internal targets; it does
// not resolve DNS, so a hostname pointing at a private IP is out of scope (the upstream
// URL is issued by our own backend).
const isBlockedInternalHost = (hostname) => {
  const host = String(hostname || '')
    .toLowerCase()
    .replace(/^\[|\]$/g, ''); // strip IPv6 brackets
  if (host === 'localhost' || host.endsWith('.localhost')) return true;
  if (host === '::1' || host === '::') return true; // IPv6 loopback / unspecified
  if (/^fe[89ab][0-9a-f]:/.test(host)) return true; // IPv6 link-local fe80::/10
  if (/^f[cd][0-9a-f]{2}:/.test(host)) return true; // IPv6 unique-local fc00::/7
  const octets = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!octets) return false;
  const a = Number(octets[1]);
  const b = Number(octets[2]);
  if (a === 0 || a === 127 || a === 10) return true; // this-host, loopback, private
  if (a === 169 && b === 254) return true; // link-local / cloud metadata
  if (a === 192 && b === 168) return true; // private
  if (a === 172 && b >= 16 && b <= 31) return true; // private
  return false;
};

// The upload target is a backend-issued signed URL (S3), which is legitimately
// cross-origin, so it can't be matched against this application's own origin. Require an
// absolute https endpoint AND reject internal/metadata hosts — blocking http/file/relative
// targets and IP-literal internal endpoints such as 169.254.169.254.
export const assertHttpsUploadUrl = (url) => {
  const uploadUrl = String(url ?? '');
  let parsed = null;
  try {
    parsed = new URL(uploadUrl);
  } catch (e) {
    parsed = null;
  }
  if (!parsed || parsed.protocol !== 'https:' || isBlockedInternalHost(parsed.hostname)) {
    throw new Error('Blocked request to an untrusted URL');
  }
  return uploadUrl;
};
